import { useEffect, useState } from "react"

import { OfferedService, Psychologist } from "utils/interfaces"
import { useBookingContext } from "modules/Booking/BookingContext"
import { CURRENCY } from "utils/constants"
import { SentryWrapper } from "utils/ThirdParty/Sentry/SentryWrapper"
import { httpClient } from "utils/infrastructure/httpClient"

type Price = Record<CURRENCY, number>

function createGetServicePrice() {
    const cache = new Map<string, Promise<Price> | Price>()
    function cacheKey(psy: Psychologist, service: OfferedService, discount: number | undefined) {
        return `${psy.hash_id}-${service.service_type_id}${discount !== undefined ? "-".concat(discount.toString()) : ""}`
    }
    function getServicePrice(
        service: OfferedService,
        psy: Psychologist,
        discount: number | undefined
    ) {
        return httpClient.post<Price>("get-session-price", {
            psy_hash_id: psy.hash_id,
            service_type_id: service.service_type_id,
            discount: discount,
        })
    }
    function toResult(value: Promise<Price> | Price) {
        if (value instanceof Promise) return value
        return Promise.resolve(value)
    }
    return function wrapper(
        service: OfferedService,
        psy: Psychologist,
        discount: number | undefined
    ): Promise<Price> {
        const key = cacheKey(psy, service, discount)
        const value = cache.get(key)
        if (value) {
            return toResult(value)
        } else {
            const request = getServicePrice(service, psy, discount).then((data) => {
                cache.set(key, data)
                return data
            })
            cache.set(key, request)
            return toResult(request)
        }
    }
}

const getServicePrice = createGetServicePrice()

export function useGetServicePrice(): [number, number, number] {
    const [{ psy, selectedDiscount, selectedService, currency }] = useBookingContext()
    const [price, setPrice] = useState(selectedService?.price ?? 0)
    const [newPrice, setNewPrice] = useState(price)
    const [defaultPrice, setDefaultPrice] = useState(price)

    const fetchServicePrice = async (withDiscount = true) => {
        if (selectedService && psy) {
            try {
                const discount = withDiscount ? selectedDiscount : undefined
                const data = await getServicePrice(selectedService, psy, discount)
                const updatedPrice = data[currency]

                if (withDiscount) {
                    setNewPrice(updatedPrice)
                } else {
                    setPrice(updatedPrice)
                }
                setDefaultPrice(updatedPrice)
            } catch (error) {
                SentryWrapper.getInstance().captureException(
                    new Error(`Request get-session-price`),
                    {
                        extra: {
                            response: error,
                        },
                    }
                )
            }
        }
    }

    useEffect(() => {
        void fetchServicePrice()
        if (selectedDiscount) {
            void fetchServicePrice(false)
        }
    }, [selectedDiscount, psy.hash_id, currency, selectedService])

    return [price, newPrice, defaultPrice]
}
